/*───────────────────────────────────────────────────────────────
 📄 File: ivrStatsExport.ts
 🧩 Description:
    CSV export utilities for IVR Statistics page.
    Extracted from IVRStatsPage to keep it lean.
────────────────────────────────────────────────────────────────*/

import type { IVRStatsSummary, IVRStatRecord } from './types';

interface DateRange {
    startDate: string;
    endDate: string;
    startTime: string;
    endTime: string;
}

function triggerDownload(csvContent: string, filename: string) {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export function exportSummaryToCSV(
    stats: IVRStatsSummary,
    dateRange: DateRange,
    onSuccess: (msg: string) => void,
    onError: (msg: string) => void,
) {
    if (!stats || stats.totalInteractions === 0) {
        onError('No data to export');
        return;
    }

    try {
        const csvRows: string[] = [];

        csvRows.push([
            'IVR Name', 'Date Range', 'Total Interactions', 'Option Presses',
            'Timeouts', 'Failures', 'Direct Dials', 'Invalid Inputs',
            'Avg Wait Time (s)', 'Avg Duration (s)', 'Most Pressed Digit', 'Most Pressed Count'
        ].join(','));

        csvRows.push([
            `"${stats.ivrName}"`,
            `"${dateRange.startDate} to ${dateRange.endDate}"`,
            stats.totalInteractions,
            stats.optionPresses,
            stats.timeouts,
            stats.failures,
            stats.directDials,
            stats.invalidInputs,
            stats.averageWaitTime.toFixed(2),
            stats.averageDuration.toFixed(2),
            stats.mostPressedDigit || 'N/A',
            stats.mostPressedCount
        ].join(','));

        csvRows.push('');
        csvRows.push(['Digit', 'Count', 'Percentage'].join(','));
        stats.digitBreakdown.forEach((item) => {
            csvRows.push([item.digit, item.count, item.percentage.toFixed(2) + '%'].join(','));
        });

        triggerDownload(
            csvRows.join('\n'),
            `ivr-stats-summary-${stats.ivrName}-${dateRange.startDate}-${dateRange.endDate}.csv`
        );
        onSuccess('CSV exported successfully');
    } catch (error) {
        onError('Failed to export CSV: ' + (error as Error).message);
    }
}

export function exportDetailsToCSV(
    callDetails: IVRStatRecord[],
    actionFilter: string,
    dateRange: DateRange,
    onSuccess: (msg: string) => void,
    onError: (msg: string) => void,
) {
    if (!callDetails || callDetails.length === 0) {
        onError('No call details to export');
        return;
    }

    try {
        const csvRows: string[] = [];

        csvRows.push([
            'Date/Time', 'Caller ID Number', 'Caller ID Name', 'Action Type',
            'Digit Pressed', 'Destination', 'Wait Time (s)', 'Total Duration (s)',
            'Attempt Number', 'IP Address', 'User Agent', 'Call UUID'
        ].join(','));

        callDetails.forEach((record) => {
            csvRows.push([
                `"${new Date(record.createdAt).toLocaleString()}"`,
                `"${record.callerIdNumber || ''}"`,
                `"${record.callerIdName || ''}"`,
                record.actionType,
                record.digitPressed || '',
                `"${record.optionDestination || ''}"`,
                record.waitTime !== null ? record.waitTime.toFixed(2) : '',
                record.totalDuration !== null ? record.totalDuration.toFixed(2) : '',
                record.attemptNumber || 1,
                `"${record.ipAddress || ''}"`,
                `"${record.userAgent || ''}"`,
                `"${record.callUuid || ''}"`
            ].join(','));
        });

        const filterSuffix = actionFilter !== 'all' ? `-${actionFilter}` : '';
        triggerDownload(
            csvRows.join('\n'),
            `ivr-calls-detail${filterSuffix}-${dateRange.startDate}-${dateRange.endDate}.csv`
        );
        onSuccess(`${callDetails.length} records exported successfully`);
    } catch (error) {
        onError('Failed to export details: ' + (error as Error).message);
    }
}
